import type {
  OntologyEntity,
  OntologyEntityType,
  OntologyViewerModel,
} from "./types";
import {
  entityDisplayName,
} from "./types";

export type RelationPropertyType = "objectProperties" | "dataProperties";

export interface OntologyRelation {
  sourceIri: string;
  propertyIri: string;
  propertyType: RelationPropertyType;
  targetIri: string;
}

export interface OntologyEntityIndexes {
  entitiesByIri: Map<string, OntologyEntity>;
  entityTypeByIri: Map<string, OntologyEntityType>;
}

export const RELATION_PROPERTY_TYPES: RelationPropertyType[] = [
  "objectProperties",
  "dataProperties",
];

export function buildOntologyEntityIndexes(model: OntologyViewerModel): OntologyEntityIndexes {
  const entitiesByIri = new Map<string, OntologyEntity>();
  const entityTypeByIri = new Map<string, OntologyEntityType>();

  (Object.keys(model.entitiesByType) as OntologyEntityType[]).forEach((entityType) => {
    model.entitiesByType[entityType].forEach((entity) => {
      if (!entitiesByIri.has(entity.iri)) {
        entitiesByIri.set(entity.iri, entity);
        entityTypeByIri.set(entity.iri, entityType);
      }
    });
  });

  return { entitiesByIri, entityTypeByIri };
}

export function buildOntologyRelations(
  model: OntologyViewerModel,
  entitiesByIri: Map<string, OntologyEntity>,
  entityTypeByIri: Map<string, OntologyEntityType>
): OntologyRelation[] {
  const relations: OntologyRelation[] = [];

  RELATION_PROPERTY_TYPES.forEach((propertyType) => {
    model.entitiesByType[propertyType].forEach((property) => {
      if (property.domain.length === 0 || property.range.length === 0) {
        return;
      }

      property.domain.forEach((sourceIri) => {
        property.range.forEach((targetIri) => {
          if (
            !entitiesByIri.has(sourceIri) ||
            !entitiesByIri.has(targetIri) ||
            entityTypeByIri.get(sourceIri) !== "classes" ||
            entityTypeByIri.get(targetIri) !== "classes"
          ) {
            return;
          }

          relations.push({
            sourceIri,
            propertyIri: property.iri,
            propertyType,
            targetIri,
          });
        });
      });
    });
  });

  return relations.sort((left, right) => {
    const leftSource = entitiesByIri.get(left.sourceIri);
    const rightSource = entitiesByIri.get(right.sourceIri);
    const sourceCompare = entityDisplayName(leftSource).localeCompare(
      entityDisplayName(rightSource)
    );
    if (sourceCompare !== 0) {
      return sourceCompare;
    }

    const leftProperty = entitiesByIri.get(left.propertyIri);
    const rightProperty = entitiesByIri.get(right.propertyIri);
    const propertyCompare = entityDisplayName(leftProperty).localeCompare(
      entityDisplayName(rightProperty)
    );
    if (propertyCompare !== 0) {
      return propertyCompare;
    }

    const leftTarget = entitiesByIri.get(left.targetIri);
    const rightTarget = entitiesByIri.get(right.targetIri);
    return entityDisplayName(leftTarget).localeCompare(entityDisplayName(rightTarget));
  });
}

export function groupRelationsBySource(
  relations: OntologyRelation[]
): Map<string, OntologyRelation[]> {
  const relationsBySource = new Map<string, OntologyRelation[]>();
  relations.forEach((relation) => {
    const sourceRelations = relationsBySource.get(relation.sourceIri) || [];
    sourceRelations.push(relation);
    relationsBySource.set(relation.sourceIri, sourceRelations);
  });
  return relationsBySource;
}

export function groupRelationsByTarget(
  relations: OntologyRelation[]
): Map<string, OntologyRelation[]> {
  const relationsByTarget = new Map<string, OntologyRelation[]>();
  relations.forEach((relation) => {
    const targetRelations = relationsByTarget.get(relation.targetIri) || [];
    targetRelations.push(relation);
    relationsByTarget.set(relation.targetIri, targetRelations);
  });
  return relationsByTarget;
}

export function relationTargetsForSource(relations: OntologyRelation[]): string[] {
  const seen = new Set<string>();
  const targets: string[] = [];

  relations.forEach((relation) => {
    if (seen.has(relation.targetIri)) {
      return;
    }
    seen.add(relation.targetIri);
    targets.push(relation.targetIri);
  });

  return targets;
}
